import type { EventRow } from './types';
import { dartRound } from './types';

/**
 * 경고 · 배너 문자열.
 *
 * ⚠️⚠️ **빈 문자열이면 화면이 그 줄을 통째로 숨긴다.** 그래서 「없음」을
 * `''` 로 돌려주는 것이 규약이고, 0% 같은 수를 적으면 **「문제 없음」으로
 * 읽힌다.** 이식할 때 이 규약을 잃으면 **배너가 늘 떠 있게 된다.**
 */

/** 집합 맵에 넣기. */
function addTo(m: Map<string, Set<string>>, k: string, v: string): void {
  let s = m.get(k);
  if (s === undefined) { s = new Set(); m.set(k, s); }
  s.add(v);
}

/** 표본 상한에 닿았나. ★ 닿으면 지표를 그대로 믿으면 안 된다. */
export function lensSampleCapNotice(
  events?: EventRow[] | null,
  sampleLimit?: number | null,
): string {
  const rows = events ?? [];
  const limit = sampleLimit ?? 0;
  if (limit <= 0 || rows.length < limit) return '';
  return (
    `표본 상한 ${limit}건에 도달했습니다. ` +
    '이보다 오래된 이벤트는 집계에서 빠져 있으므로 지표를 그대로 믿으면 안 됩니다. ' +
    '기간 필터를 좁히거나 날짜 조건을 서버 쿼리로 옮기세요.'
  );
}

/** 이 기간에 회차가 있나. */
export function lensRecentRunsNote(events?: EventRow[] | null): string {
  for (const e of events ?? []) {
    if (e.eventName === 'run_started') return '';
  }
  return '이 기간에 회차가 없습니다.';
}

/** ★ 완주 0일 때 「저작된 결말이 없다」로 읽히지 않게 한다. */
export function lensEndingNotice(events?: EventRow[] | null): string {
  let started = 0;
  let reached = 0;
  for (const e of events ?? []) {
    if (e.eventName === 'run_started') started++;
    if (e.eventName === 'ending_reached') reached++;
  }
  if (reached > 0) return '';
  if (started === 0) return '';
  return (
    `완주한 회차가 없어 목록이 비어 있습니다 — 시작한 ${started}회차 중 ` +
    '엔딩에 도달한 회차가 0입니다. 저작된 결말이 없다는 뜻이 아닙니다.'
  );
}

/**
 * ⚠️⚠️ **「실패가 0건」과 「검증이 안 돎」을 가른다.**
 * 안 가르면 `0.0% 정상` 초록이 떠서 **빈 화면보다 나쁘다 — 정상이라고
 * 거짓말한다.**
 */
export function lensNarrationNotice(events?: EventRow[] | null): string {
  let turns = 0;
  let validated = 0;
  let sawFlag = false;
  let failed = 0;
  for (const e of events ?? []) {
    if (
      e.eventName === 'action_interpreted' ||
      e.eventName === 'unsupported_action_requested'
    ) {
      turns++;
      if (e.narrationValidated !== null) {
        sawFlag = true;
        if (e.narrationValidated === true) validated++;
      }
    }
    if (e.eventName === 'narration_validation_failed') failed++;
  }
  if (failed > 0) return '';
  if (turns === 0) return '';
  if (sawFlag && validated === 0) {
    return (
      `서술 검증이 한 번도 돌지 않아 목록이 비어 있습니다 — 행동 턴 ` +
      `${turns}건 전부 검증되지 않았습니다. 실패가 0건이라는 뜻이 아닙니다.`
    );
  }
  return '';
}

/** 가장 많이 빠져나간 곳. */
export function lensFunnelNotice(events?: EventRow[] | null): string {
  const entered = new Map<string, Set<string>>();
  for (const e of events ?? []) {
    if (e.eventName !== 'scene_entered') continue;
    if (e.sceneId === '' || e.runId === '') continue;
    addTo(entered, e.sceneId, e.runId);
  }
  if (entered.size < 2) return '';
  const keys = [...entered.keys()].sort();
  const counts = keys.map((k) => entered.get(k)!.size);

  let dropAt = -1;
  let dropMax = 0;
  for (let i = 1; i < counts.length; i++) {
    const d = counts[i - 1]! - counts[i]!;
    if (d > dropMax) { dropMax = d; dropAt = i; }
  }
  if (dropAt < 0 || dropMax === 0) return '';
  const prev = keys[dropAt - 1]!;
  const here = keys[dropAt]!;
  const pct = ((dropMax * 100) / counts[dropAt - 1]!).toFixed(1);
  return (
    `가장 많이 빠져나간 곳은 ${prev} 입니다 — ` +
    `${prev} 에 들어온 ${counts[dropAt - 1]}회차 중 ${dropMax}회차(${pct}%)가 ` +
    `다음 장면(${here})에 도달하지 못했습니다.`
  );
}

/**
 * 시작 장면이 섞였나.
 *
 * ★ 섞이면 완주율·턴 수·장면 분포를 **한 값으로 읽을 수 없다.**
 */
export function lensStartSceneNotice(events?: EventRow[] | null): string {
  const byScene = new Map<string, Set<string>>();
  for (const e of events ?? []) {
    if (e.eventName !== 'run_started') continue;
    addTo(byScene, e.sceneId === '' ? '(미상)' : e.sceneId, e.runId);
  }
  if (byScene.size < 2) return '';

  const keys = [...byScene.keys()].sort(
    (a, b) => byScene.get(b)!.size - byScene.get(a)!.size,
  );
  const parts = keys.map((k) => `${k} ${byScene.get(k)!.size}회차`);
  const total = [...byScene.values()].reduce((a, b) => a + b.size, 0);
  return (
    `표본 ${total}회차가 서로 다른 장면에서 시작했습니다 — ` +
    `${parts.join(' · ')}. 시작 장면이 다르면 완주율·턴 수·장면 분포를 ` +
    `한 값으로 읽을 수 없습니다. 아래 지표는 그 회차들을 섞어 셉니다.`
  );
}

/**
 * 플래그 획득률이 바닥인 장면.
 *
 * ⚠️ **표본 20회차 미만은 판단하지 않는다** — 비율이 흔들린다.
 */
/**
 * 한 창에 **이벤트 스키마 판본이 섞였는지** 밝힌다.
 *
 * ★★★ 왜 필요한가 — **이벤트는 발행 시점에 굳는다**(개발 회신 41 9절).
 * 새 필드는 **옛 회차에 소급되지 않는다.** 그래서 「막힌 것」처럼 새 필드에
 * 기대는 값은 **새 회차가 쌓이는 동안 천천히 올라가고**, 읽는 사람에게는
 * **콘텐츠가 나빠진 것처럼** 보인다.
 *
 * ⚠️ 실측(2026-10-04) — 쓸 만한 28회차 중 `1.0.0` 이 27개였다.
 *
 * ★★ **값을 숨기지 않는다.** 하한을 걸어 「판단 보류」로 바꾸는 대신
 * **표본을 옆에 세운다** — 시작 장면 배너와 같은 설계다.
 *
 * ⚠️⚠️ 그래서 **켜고 끌 것이 없다.** 판본이 하나로 모이면 배너가 **저절로
 * 사라진다.** 개발 회신 46 3절 ⑷ ①③(판본 가름)은 그때 필터로 바꾼다.
 *
 * ★ 판본은 **회차마다 가장 낮은 것**을 잡는다 — 판이 바뀌는 순간 한 회차
 * 안에서 섞일 수 있다. ⚠️ 가장 높은 것을 잡으면 「새 필드가 다 있다」고
 * 거짓말한다.
 */
export function lensSchemaNotice(events?: EventRow[] | null): string {
  // 회차 → 그 회차의 가장 낮은 판본
  const lowest = new Map<string, string>();
  for (const e of events ?? []) {
    if (e.runId === '' || e.schemaVersion === '') continue;
    const cur = lowest.get(e.runId);
    // ⚠️ 문자열 비교다 — `'1.10.0'` 이 나오면 틀린다. 지금 판본은 한 자리라
    //    괜찮고, 두 자리가 나오면 여기를 고친다.
    if (cur === undefined || e.schemaVersion < cur) lowest.set(e.runId, e.schemaVersion);
  }
  if (lowest.size === 0) return '';

  const byVer = new Map<string, number>();
  for (const v of lowest.values()) byVer.set(v, (byVer.get(v) ?? 0) + 1);
  // ★ 하나뿐이면 할 말이 없다 — 배너를 숨긴다.
  if (byVer.size < 2) return '';

  const parts = [...byVer.keys()]
    .sort()
    .map((v) => `${v} ${byVer.get(v)}회차`);
  return (
    `표본 ${lowest.size}회차에 이벤트 판본이 섞여 있습니다 — ${parts.join(' · ')}. ` +
    `새 필드는 옛 회차에 소급되지 않아, 그 필드에 기대는 값은 새 회차가 ` +
    `쌓이는 동안 올라갑니다. 아래 지표는 섞어 셉니다.`
  );
}

export function lensFlagNotice(
  events?: EventRow[] | null,
  floorPct?: number | null,
): string {
  const floor = floorPct ?? 5;
  const entered = new Map<string, Set<string>>();
  const got = new Map<string, Set<string>>();
  for (const e of events ?? []) {
    const s = e.sceneId;
    if (s === '') continue;
    if (e.eventName === 'scene_entered') addTo(entered, s, e.runId);
    else if (e.eventName === 'flag_acquired') {
      addTo(got, s, e.runId);
      addTo(entered, s, e.runId);
    }
  }
  const low: string[] = [];
  for (const k of entered.keys()) {
    const n = entered.get(k)!.size;
    if (n < 20) continue;
    const rate = ((got.get(k)?.size ?? 0) * 100) / n;
    if (rate <= floor) low.push(`${k} ${rate.toFixed(1)}%`);
  }
  if (low.length === 0) return '';
  low.sort();
  return (
    `플래그 획득률이 ${floor.toFixed(0)}% 이하인 장면 ` +
    `${low.length}개 — ${low.join(' · ')}. 단서를 놓았는데 아무도 찾지 ` +
    `못하고 있다면 어포던스가 전달되지 않은 것이고, 그 뒤 분기가 함께 죽습니다.`
  );
}

/**
 * 막힘 지점 배너.
 *
 * ⚠️ **잘라낸 개수를 밝힌다** — 조용히 자르면 목록에 없는 곳이 문제가 없었던
 * 것으로 읽힌다. ★ `shown` 은 `lensStuckPoints` 에도 있다 — **같이 고쳐야**
 * 「나머지 N곳」이 맞는다.
 * ★ 배너는 **최대 깊이**를, 목록은 **비율**을 기준으로 하므로 배너가 가리키는
 *   지점이 맨 위 줄이 아닐 수 있다 — 그 사실을 배너 안에 적는다.
 */
export function lensStuckNotice(
  events?: EventRow[] | null,
  floorAttempts?: number | null,
): string {
  const floor = dartRound(floorAttempts ?? 3);
  const perRun = new Map<string, Map<string, number>>();
  let rejects = 0;
  let noProp = 0;
  let reqOnly = 0;
  for (const e of events ?? []) {
    if (e.eventName !== 'unsupported_action_requested') continue;
    rejects++;
    if (e.propId === '') {
      noProp++;
      if (e.requestedPropId !== '') reqOnly++;
      continue;
    }
    const prop = e.propId.startsWith('p_') ? e.propId.slice(2) : e.propId;
    const key = `${e.sceneId} 의 ${prop}`;
    let m = perRun.get(key);
    if (m === undefined) { m = new Map(); perRun.set(key, m); }
    m.set(e.runId, (m.get(e.runId) ?? 0) + 1);
  }
  if (rejects === 0) return '';

  // ⚠️ **「목록이 빈 것」과 「막힘이 없는 것」을 가른다.**
  if (noProp === rejects) {
    return (
      `거부가 ${rejects}건 있는데 대상(prop_id)이 한 건도 실려 오지 ` +
      `않았습니다. 그 값이 없으면 어느 대상에서 반복해 막혔는지 셀 수 없습니다 — ` +
      `아래 목록이 빈 것은 막힘이 없다는 뜻이 아닙니다.`
    );
  }

  let worstKey = '';
  let worstDepth = 0;
  let spots = 0;
  for (const [key, m] of perRun) {
    let hit = false;
    for (const c of m.values()) {
      if (c >= floor) hit = true;
      if (c > worstDepth) { worstDepth = c; worstKey = key; }
    }
    if (hit) spots++;
  }

  const tail =
    noProp === 0
      ? ''
      : ` 대상을 짚지 못한 거부 ${noProp}건은 이 목록에 없습니다` +
        `(그중 ${reqOnly}건은 콜①만 대상을 말했습니다).`;

  if (spots === 0) {
    return worstDepth === 0
      ? ''
      : `같은 대상에 ${floor}회 이상 쌓인 지점은 없습니다. 가장 많은 곳도 ` +
        `${worstKey} ${worstDepth}회입니다.${tail}`;
  }
  const shown = 6;
  const hidden = spots - shown;
  const extra =
    hidden > 0 ? ` 목록에는 비율 상위 ${shown}곳만 보이며 나머지 ${hidden}곳은 생략했습니다.` : '';
  return (
    `같은 대상에 거부가 ${floor}회 이상 쌓인 지점이 ${spots}곳입니다.${extra} ` +
    `가장 깊은 곳은 ${worstKey} 로, 한 회차에서 ${worstDepth}번 거부됐습니다 ` +
    `(목록은 비율 순이라 이 지점이 맨 위가 아닐 수 있습니다).${tail}`
  );
}

/**
 * 아무도 못 넘은 DC 경고.
 *
 * ★★ **규칙 계층 상수를 화면에 복사하지 않는 것이 요점이다.** 「DC 21 이상은
 * 불가능」을 하드코딩하면 안 된다 — `roll_result` 가 능력치·숙련 보정까지 더한
 * 최종 판정치라 동사마다 상한이 다르다(조사는 23까지 간다).
 * → **표본이 실제로 넘어본 최댓값을 동사별로** 쓴다. 보정표를 몰라도 맞고
 *   보정이 바뀌어도 따라온다.
 * ⚠️ 전체 최댓값 하나만 쓰면 조사가 23을 내는 순간 이동의 천장까지 23으로
 *    올라가 **이동이 못 넘는 DC 를 넘을 수 있다고 말하게 된다.**
 */
export function lensCheckDcNotice(events?: EventRow[] | null): string {
  const verbOf = new Map<string, string>();
  for (const e of events ?? []) {
    if (e.eventName !== 'action_interpreted') continue;
    if (e.actionType === '') continue;
    verbOf.set(`${e.runId}#${e.turnIndex}`, e.actionType);
  }

  // ⚠️ 능력치 개편 경계 — 그 이전 굴림은 천장 계산에서 뺀다.
  const abilityBoundary = Date.UTC(2026, 8, 19, 13, 12, 52);
  let rolls = 0;
  const maxOf = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.eventName !== 'check_resolved') continue;
    if (e.kind !== '' && e.kind !== 'roll') continue;
    // 원본에는 여기 `if (e.kind == 'bypass') continue;` 가 한 줄 더 있다.
    // TS 가 도달 불가를 증명했다(TS2367) — 바로 위 줄이 'roll' 과 빈 값
    // 외를 전부 걸러내므로 bypass 는 여기 닿지 않는다. 값은 같다.
    rolls++;
    const v = e.actionType !== '' ? e.actionType : (verbOf.get(`${e.runId}#${e.turnIndex}`) ?? '(동사 미상)');
    const at = e.occurredAt;
    if (at !== null && at.getTime() < abilityBoundary) continue;
    const r = e.rollResult;
    if (r > (maxOf.get(v) ?? 0)) maxOf.set(v, r);
  }
  if (rolls === 0) return '';

  let high = 0;
  const overOf = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.eventName !== 'check_resolved') continue;
    if (e.kind !== '' && e.kind !== 'roll') continue;
    // 원본에는 여기 `if (e.kind == 'bypass') continue;` 가 한 줄 더 있다.
    // TS 가 도달 불가를 증명했다(TS2367) — 바로 위 줄이 'roll' 과 빈 값
    // 외를 전부 걸러내므로 bypass 는 여기 닿지 않는다. 값은 같다.
    const v = e.actionType !== '' ? e.actionType : (verbOf.get(`${e.runId}#${e.turnIndex}`) ?? '(동사 미상)');
    const cap = maxOf.get(v) ?? 0;
    if (cap === 0 || e.dc <= cap) continue;
    high++;
    overOf.set(v, (overOf.get(v) ?? 0) + 1);
  }
  if (high === 0) return '';
  const keys = [...overOf.keys()].sort((a, b) => overOf.get(b)! - overOf.get(a)!);
  const top = keys
    .slice(0, 3)
    .map((k) => `${k} 최고 ${maxOf.get(k)} · 초과 ${overOf.get(k)}건`)
    .join(' · ');
  const pct = ((high * 100) / rolls).toFixed(1);
  return (
    `이 표본에서 그 동사가 낸 최고 판정치보다 높은 DC 가 ${high}건(${pct}%) ` +
    `있습니다 — ${top}. 판정치는 주사위 눈이 아니라 능력치·숙련 보정까지 더한 ` +
    `값이라 동사마다 상한이 다릅니다. 표본이 적으면 최고치도 낮게 잡히니 ` +
    `굴림 수와 함께 보십시오(굴림 ${rolls}건).`
  );
}

/** 천 단위 구분자(요약 전용). */
function comma(v: number): string {
  const s = String(v);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return out;
}

/**
 * 소스 배지.
 *
 * ⚠️⚠️ **집계가 보는 목록이 확정된 뒤에 만든다.** 창·필터 앞에서 돌면 배지는
 * 「21회차」인데 아래 지표는 필터 뒤 1회차를 써서, 화면에 **완주율 100.0%** 와
 * **21회차** 가 나란히 떠 「21회차 전부 완주」로 읽힌다(실측).
 */
export function lensSourceBadge(
  events?: EventRow[] | null,
  mode?: string | null,
  runWindow?: number | null,
): string {
  const rows = events ?? [];
  const label = (mode ?? 'mock') === 'mock' ? '목업 데이터' : 'Firestore';
  if (rows.length === 0) return `${label} · 데이터 없음`;

  const runs = new Set<string>();
  for (const e of rows) if (e.runId !== '') runs.add(e.runId);
  const n = runs.size;
  const cap = dartRound(runWindow ?? 0);
  // ★ 값을 숨기지 않는다 — 표본을 옆에 세운다. `1회차 (창 100)` 옆의
  //   `100.0%` 는 스스로 설명한다.
  if (cap > 0 && n < cap) return `${label} · ${n}회차 (창 ${cap})`;
  return `${label} · ${n}회차`;
}

/** 소스 요약. ★ 설정 화면은 **걸러진 수가 아니라 소스의 실제 건수**를 본다. */
export function lensSourceSummary(
  events?: EventRow[] | null,
  mode?: string | null,
): string {
  const rows = events ?? [];
  const label = (mode ?? 'mock') === 'mock' ? '목업 데이터' : 'Firestore';
  if (rows.length === 0) return `${label} · 이벤트 없음`;

  const runs = new Set<string>();
  let first: Date | null = null;
  let last: Date | null = null;
  for (const e of rows) {
    if (e.runId !== '') runs.add(e.runId);
    const at = e.occurredAt;
    if (at === null) continue;
    if (first === null || at.getTime() < first.getTime()) first = at;
    if (last === null || at.getTime() > last.getTime()) last = at;
  }

  const day = (v: Date | null): string =>
    v === null
      ? '-'
      : `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;

  return (
    `${label} · 이벤트 ${comma(rows.length)}건 · ${comma(runs.size)}회차 · ` +
    `${day(first)} ~ ${day(last)}`
  );
}
